import React, { useState } from 'react';
import { Lightning, Printer, DownloadSimple, EnvelopeSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';
import api from '../../utils/api';

export const SidepotPanel = ({
  show, selectedSidepot, sidepotResults, sidepotLoading,
  sortedEntries, onLoadSidepotResults, onToggleSidepot, onCalculateSidepotPayouts
}) => {
  const [placesPaid, setPlacesPaid] = useState(1);
  const [calculating, setCalculating] = useState(false);
  // Per-division pool % — mirrors the regular-class Payouts panel. Only
  // surfaced when the selected sidepot has division_type set to 2D/3D/4D.
  const [divisionPoolPct, setDivisionPoolPct] = useState({});
  const spDivNames = selectedSidepot?.division_type === '4D' ? ['1D','2D','3D','4D'] :
    selectedSidepot?.division_type === '3D' ? ['1D','2D','3D'] :
    selectedSidepot?.division_type === '2D' ? ['1D','2D'] : [];
  const spHasDivs = spDivNames.length > 0;

  // Seed even split when the sidepot switches so the inputs start at a
  // sensible default (25/25/25/25 for 4D, etc.) instead of 0s.
  React.useEffect(() => {
    if (!spHasDivs) { setDivisionPoolPct({}); return; }
    const even = Math.round(100 / spDivNames.length);
    const last = 100 - even * (spDivNames.length - 1);
    const seeded = {};
    spDivNames.forEach((n, i) => { seeded[n] = i === spDivNames.length - 1 ? last : even; });
    setDivisionPoolPct(seeded);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSidepot?.id, selectedSidepot?.division_type]);

  const handleCalculate = async () => {
    if (!selectedSidepot) return;
    if (spHasDivs) {
      const total = spDivNames.reduce((s, n) => s + (divisionPoolPct[n] || 0), 0);
      if (Math.abs(total - 100) > 1) {
        toast.error(`Division pool % must sum to 100 (currently ${total}%)`);
        return;
      }
    }
    setCalculating(true);
    try {
      await onCalculateSidepotPayouts(selectedSidepot.id, placesPaid, spHasDivs ? divisionPoolPct : {});
    } finally {
      setCalculating(false);
    }
  };

  const getPayoutForPlace = (place) => {
    if (!sidepotResults?.payouts) return null;
    return sidepotResults.payouts.find(p => p.place === place);
  };

  // Division color palette — matches results export color coding
  const DIVISION_COLORS = { '1D': '#D6EAF8', '2D': '#F9D6E2', '3D': '#FCF3CF', '4D': '#D4EFDF' };
  const divisionAccent = (name) => {
    if (!name) return null;
    const key = String(name).trim().toUpperCase();
    return Object.keys(DIVISION_COLORS).find(k => key.startsWith(k)) ? DIVISION_COLORS[Object.keys(DIVISION_COLORS).find(k => key.startsWith(k))] : null;
  };

  // When results have a division_type set, group the result rows by division
  // for rendering (mirrors the main results Overall tab behaviour).
  const hasDivisions =
    sidepotResults &&
    sidepotResults.division_type &&
    sidepotResults.division_type !== 'NONE' &&
    sidepotResults.division_payouts &&
    Object.keys(sidepotResults.division_payouts).length > 0;

  const resultsByDivision = React.useMemo(() => {
    if (!hasDivisions || !sidepotResults?.results) return {};
    const grouped = {};
    sidepotResults.results.forEach(r => {
      const div = r.division || 'Unassigned';
      (grouped[div] = grouped[div] || []).push(r);
    });
    // Sort each division by its division_place (fallback to place)
    Object.keys(grouped).forEach(div => {
      grouped[div].sort((a, b) => (a.division_place ?? a.place ?? 999) - (b.division_place ?? b.place ?? 999));
    });
    return grouped;
  }, [sidepotResults, hasDivisions]);

  const getDivPayout = (divName, place) => {
    const divPay = sidepotResults?.division_payouts?.[divName];
    if (!divPay?.payouts) return null;
    return divPay.payouts.find(p => p.place === place);
  };

  // === Export / Print / Email ===
  const exportSidepotExcel = async () => {
    if (!selectedSidepot) return;
    try {
      const resp = await api.get(`/sidepot-results/${selectedSidepot.id}/xlsx`, { responseType: 'blob' });
      const blob = new Blob([resp.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const safe = (selectedSidepot.name || 'sidepot').replace(/[^a-z0-9]+/gi, '_');
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${safe}_sidepot_results.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch {
      toast.error('Failed to export sidepot results');
    }
  };

  const emailSidepot = async () => {
    if (!selectedSidepot) return;
    const recipients = window.prompt('Recipient email(s) — comma-separated:');
    if (!recipients) return;
    const list = recipients.split(',').map(s => s.trim()).filter(Boolean);
    if (!list.length) return;
    try {
      const { data } = await api.post(`/sidepot-results/${selectedSidepot.id}/email`, {
        recipients: list, include_csv: true, include_pdf: true,
      });
      toast.success(`Emailed to ${data.sent} recipient${data.sent !== 1 ? 's' : ''}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to email sidepot results');
    }
  };

  const printSidepot = () => {
    if (!sidepotResults || !sidepotResults.results?.length) return;
    const title = `${show?.name || ''} — ${selectedSidepot.name} (Sidepot)`;
    let bodyHtml = '';
    if (hasDivisions) {
      Object.keys(resultsByDivision).sort().forEach(divName => {
        const color = divisionAccent(divName) || '#E5E7EB';
        const pool = sidepotResults.division_payouts?.[divName]?.pool ?? 0;
        bodyHtml += `<tr class="divhead"><td colspan="5" style="background:${color} !important;color:#111;padding:8px 10px;font-weight:700;-webkit-print-color-adjust:exact;print-color-adjust:exact;">${divName} · Pool $${pool.toFixed(0)}</td></tr>`;
        resultsByDivision[divName].forEach(r => {
          const dp = r.division_place ?? r.place;
          const pay = getDivPayout(divName, dp);
          const payStr = pay && pay.amount > 0 ? `$${pay.amount.toFixed(0)}` : '';
          bodyHtml += `<tr style="background:${color} !important;-webkit-print-color-adjust:exact;print-color-adjust:exact;">
            <td style="text-align:center;font-weight:700">${divName}-${dp}</td>
            <td>${(r.rider_name || '').replace(/</g, '&lt;')}</td>
            <td>${(r.horse_name || '').replace(/</g, '&lt;')}</td>
            <td style="text-align:right;font-family:monospace">${r.final_time?.toFixed(3) ?? ''}</td>
            <td style="text-align:right;font-family:monospace;color:#1e8e3e;font-weight:700">${payStr}</td>
          </tr>`;
        });
      });
    } else {
      sidepotResults.results.forEach(r => {
        const pay = getPayoutForPlace(r.place);
        const payStr = pay && pay.amount > 0 ? `$${pay.amount.toFixed(0)}` : '';
        bodyHtml += `<tr>
          <td style="text-align:center;font-weight:700">${r.tied ? `T-${r.place}` : r.place}</td>
          <td>${(r.rider_name || '').replace(/</g, '&lt;')}</td>
          <td>${(r.horse_name || '').replace(/</g, '&lt;')}</td>
          <td style="text-align:right;font-family:monospace">${r.final_time?.toFixed(3) ?? ''}</td>
          <td style="text-align:right;font-family:monospace;color:#1e8e3e;font-weight:700">${payStr}</td>
        </tr>`;
      });
    }
    const html = `<!doctype html><html><head><title>${title}</title><meta charset="utf-8"/>
      <style>
        *{-webkit-print-color-adjust:exact !important;print-color-adjust:exact !important;color-adjust:exact !important}
        body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;color:#111;padding:24px;margin:0}
        h1{margin:0 0 4px;font-size:20px}
        .sub{color:#666;font-size:12px;margin-bottom:16px}
        table{width:100%;border-collapse:collapse;font-size:12px}
        th{text-align:left;padding:7px 9px;background:#0A0A0A;color:#fff;font-weight:700}
        td{padding:7px 9px;border-bottom:1px solid #eee}
        @media print{@page{margin:0.4in}}
      </style></head><body>
      <h1>${(show?.name || '').replace(/</g, '&lt;')}</h1>
      <div class="sub">${selectedSidepot.name} · Sidepot Results · ${new Date().toLocaleString()}</div>
      <table>
        <thead><tr>
          <th style="width:70px;text-align:center">Place</th>
          <th>Rider</th>
          <th>Horse</th>
          <th style="width:90px;text-align:right">Time</th>
          <th style="width:100px;text-align:right">Payout</th>
        </tr></thead>
        <tbody>${bodyHtml}</tbody>
      </table>
      <script>window.onload=()=>{window.print()};</script>
      </body></html>`;
    const w = window.open('', '_blank');
    if (w) { w.document.write(html); w.document.close(); }
  };

  return (
    <div className="p-5" data-testid="sidepots-panel">
      <p className="text-xs text-[#9E9E9E] mb-4">
        Assign riders to sidepots. Their times will automatically roll over.
      </p>

      <div className="space-y-2 mb-5">
        {(show.sidepots || []).map((sp) => (
          <button
            key={sp.id}
            onClick={() => { onLoadSidepotResults(sp); }}
            data-testid={`sidepot-btn-${sp.id}`}
            className={`w-full text-left p-3 rounded-lg border transition-all ${
              selectedSidepot?.id === sp.id
                ? 'bg-[#FACC15]/10 border-[#FACC15] text-white'
                : 'bg-[#0A0A0A] border-[#262626] text-[#9E9E9E] hover:border-[#FACC15]/50'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Lightning size={14} weight="fill" className="text-[#FACC15]" />
                <span className="font-semibold text-sm">{sp.name}</span>
              </div>
              <span className="text-xs">${sp.entry_fee}</span>
            </div>
            <p className="text-xs mt-0.5 opacity-60">
              {sortedEntries.filter(e => (e.sidepot_ids || []).includes(sp.id)).length} entered
            </p>
          </button>
        ))}
      </div>

      {selectedSidepot && (
        <>
          <div className="border-t border-[#262626] pt-4 mb-3">
            <h4 className="text-sm font-bold text-[#FACC15] mb-1">{selectedSidepot.name}</h4>
            <p className="text-xs text-[#9E9E9E] mb-3">Check riders to enroll in this sidepot</p>
          </div>

          <div className="max-h-[200px] overflow-y-auto space-y-1 mb-4">
            {sortedEntries.map((entry) => {
              const isEnrolled = (entry.sidepot_ids || []).includes(selectedSidepot.id);
              return (
                <div key={entry.id} onClick={() => onToggleSidepot(entry, selectedSidepot.id)}
                  className={`flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
                    isEnrolled ? 'bg-[#FACC15]/10 border border-[#FACC15]/30' : 'bg-[#0A0A0A] border border-transparent hover:border-[#262626]'
                  }`} data-testid={`sidepot-entry-${entry.id}`}>
                  <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${isEnrolled ? 'bg-[#FACC15] border-[#FACC15]' : 'border-[#555]'}`}>
                    {isEnrolled && <span className="text-black text-[10px] font-bold">&#10003;</span>}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-white text-sm font-semibold truncate">{entry.rider_name}</p>
                    {entry.horse_name && <p className="text-[#9E9E9E] text-xs truncate">{entry.horse_name}</p>}
                  </div>
                  {entry.draw_number && <span className="text-[#0096FF] text-xs font-bold">#{entry.draw_number}</span>}
                </div>
              );
            })}
          </div>

          {/* Payout Controls */}
          {sidepotResults && sidepotResults.total_scored > 0 && (
            <div className="border-t border-[#262626] pt-4 mb-4">
              <div className="flex items-center gap-3 mb-3">
                <p className="text-xs text-[#9E9E9E]">
                  {sidepotResults.total_entries} entries &bull; ${selectedSidepot.entry_fee}/entry &bull; Pool: <span className="text-[#34C759] font-semibold">${sidepotResults.total_pool?.toFixed(0) || '0'}</span>
                </p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <label className="text-xs text-[#9E9E9E]">Places Paid:</label>
                <select
                  value={placesPaid}
                  onChange={(e) => setPlacesPaid(parseInt(e.target.value))}
                  className="px-2 py-1 bg-[#0A0A0A] border border-[#262626] rounded text-white text-sm focus:outline-none focus:border-[#FACC15]"
                  data-testid="sidepot-places-paid"
                >
                  {Array.from({ length: Math.min(sidepotResults.total_scored || 1, 15) }, (_, i) => i + 1).map(n => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
                <button
                  onClick={handleCalculate}
                  disabled={calculating}
                  className="px-3 py-1 bg-[#FACC15] hover:bg-[#FACC15]/80 text-black text-xs font-bold rounded transition-colors disabled:opacity-50"
                  data-testid="sidepot-calculate-btn"
                >
                  {calculating ? 'Calculating...' : 'Calculate & Save'}
                </button>
              </div>
              {spHasDivs && (
                <div className="mt-3 pt-3 border-t border-[#262626]/70" data-testid="sidepot-division-pct">
                  <p className="text-[10px] font-semibold text-[#FACC15] uppercase tracking-wider mb-2">Pool % per division</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {spDivNames.map((n) => (
                      <div key={n} className="flex items-center gap-1.5">
                        <span className="text-xs text-white font-bold w-7">{n}</span>
                        <div className="flex-1 relative">
                          <input
                            type="number" min="0" max="100"
                            value={divisionPoolPct[n] ?? 0}
                            onChange={(e) => setDivisionPoolPct((prev) => ({ ...prev, [n]: parseFloat(e.target.value) || 0 }))}
                            className="w-full px-2 py-1 bg-[#0A0A0A] border border-[#262626] rounded text-white text-xs text-right pr-5 focus:outline-none focus:border-[#FACC15]"
                            data-testid={`sidepot-div-pct-${n.toLowerCase()}`}
                          />
                          <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[#555] text-[10px]">%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  {(() => {
                    const total = spDivNames.reduce((s, n) => s + (divisionPoolPct[n] || 0), 0);
                    const ok = Math.abs(total - 100) < 1;
                    return (
                      <p className={`text-[10px] mt-1.5 ${ok ? 'text-[#34C759]' : 'text-[#FF3B30]'}`} data-testid="sidepot-pct-total">
                        Total: {total}% {ok ? '' : '(must equal 100%)'}
                      </p>
                    );
                  })()}
                </div>
              )}
            </div>
          )}

          {/* Results with Payouts */}
          {sidepotResults && sidepotResults.results.length > 0 && (
            <div className="border-t border-[#262626] pt-4">
              <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                <h4 className="text-sm font-bold text-white flex items-center gap-2 flex-wrap">
                  {sidepotResults.sidepot_name} Results
                  <span className="text-[#9E9E9E] font-normal text-xs">{sidepotResults.total_scored}/{sidepotResults.total_entries} scored</span>
                  {hasDivisions && (
                    <span className="text-[9px] font-bold uppercase tracking-wider bg-[#0096FF]/20 text-[#0096FF] px-2 py-0.5 rounded">
                      {sidepotResults.division_type}
                    </span>
                  )}
                </h4>
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={printSidepot}
                    className="flex items-center gap-1 px-2.5 py-1.5 bg-[#0A0A0A] border border-[#262626] text-[#9E9E9E] rounded-md text-[11px] font-semibold hover:text-white hover:border-[#0096FF]/50 transition-colors"
                    data-testid="sidepot-print-btn"
                    title="Print sidepot results"
                  >
                    <Printer size={12} weight="bold" /> Print
                  </button>
                  <button
                    onClick={exportSidepotExcel}
                    className="flex items-center gap-1 px-2.5 py-1.5 bg-[#0A0A0A] border border-[#262626] text-[#9E9E9E] rounded-md text-[11px] font-semibold hover:text-white hover:border-[#34C759]/50 transition-colors"
                    data-testid="sidepot-export-btn"
                    title="Download color-coded Excel"
                  >
                    <DownloadSimple size={12} weight="bold" /> Excel
                  </button>
                  <button
                    onClick={emailSidepot}
                    className="flex items-center gap-1 px-2.5 py-1.5 bg-[#0A0A0A] border border-[#262626] text-[#9E9E9E] rounded-md text-[11px] font-semibold hover:text-white hover:border-[#FF9500]/50 transition-colors"
                    data-testid="sidepot-email-btn"
                    title="Email sidepot results"
                  >
                    <EnvelopeSimple size={12} weight="bold" /> Email
                  </button>
                </div>
              </div>

              {hasDivisions ? (
                <div className="space-y-4 max-h-[400px] overflow-y-auto">
                  {Object.keys(resultsByDivision).sort().map(divName => {
                    const divPay = sidepotResults.division_payouts?.[divName];
                    const accent = divisionAccent(divName) || '#9E9E9E';
                    return (
                      <div key={divName} data-testid={`sidepot-division-${divName}`}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span
                              className="text-xs font-black px-2 py-0.5 rounded"
                              style={{ background: accent + '33', color: accent }}
                            >
                              {divName}
                            </span>
                            <span className="text-[10px] text-[#9E9E9E]">
                              Pool: <span className="text-[#34C759] font-bold">${(divPay?.pool ?? 0).toFixed(0)}</span>
                              {' '}· Places paid: {divPay?.places_paid ?? 0}
                            </span>
                          </div>
                        </div>
                        <div className="space-y-1">
                          {resultsByDivision[divName].map(r => {
                            const dp = r.division_place ?? r.place;
                            const payout = getDivPayout(divName, dp);
                            return (
                              <div
                                key={r.entry_id}
                                className="flex items-center justify-between px-3 py-1.5 rounded-lg bg-[#0A0A0A] border border-[#262626]"
                                style={{ borderLeft: `3px solid ${accent}` }}
                                data-testid={`sidepot-result-${divName}-${dp}`}
                              >
                                <div className="flex items-center gap-2 min-w-0">
                                  <span className="text-sm font-black flex-shrink-0" style={{ color: accent, minWidth: 36 }}>
                                    {divName}-{dp}
                                  </span>
                                  <span className="text-white text-sm truncate">{r.rider_name}</span>
                                  {r.horse_name && <span className="text-[#9E9E9E] text-xs truncate">{r.horse_name}</span>}
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                  <span className="text-white font-bold text-sm" style={{ fontFamily: 'JetBrains Mono' }}>
                                    {r.final_time?.toFixed(3)}
                                  </span>
                                  {payout && payout.amount > 0 && (
                                    <span className="px-2 py-0.5 bg-[#34C759]/15 text-[#34C759] rounded text-xs font-bold">
                                      ${payout.amount.toFixed(0)}
                                    </span>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <>
                  <div className="space-y-1 max-h-[300px] overflow-y-auto">
                    {sidepotResults.results.map((r) => {
                      const payout = getPayoutForPlace(r.place);
                      return (
                        <div key={r.entry_id} className={`flex items-center justify-between px-3 py-2 rounded-lg ${
                          r.place === 1 ? 'bg-[#FFD700]/10 border border-[#FFD700]/30' :
                          r.place === 2 ? 'bg-[#C0C0C0]/10 border border-[#C0C0C0]/30' :
                          r.place === 3 ? 'bg-[#CD7F32]/10 border border-[#CD7F32]/30' : 'bg-[#0A0A0A]'
                        }`} data-testid={`sidepot-result-${r.place}`}>
                          <div className="flex items-center gap-2 min-w-0">
                            <span className={`text-base font-black flex-shrink-0 ${r.place === 1 ? 'text-[#FFD700]' : r.place === 2 ? 'text-[#C0C0C0]' : r.place === 3 ? 'text-[#CD7F32]' : 'text-[#9E9E9E]'}`}>{r.tied ? `T-${r.place}` : r.place}</span>
                            <span className="text-white text-sm truncate">{r.rider_name}</span>
                            {r.tied && <span className="text-[#FF9500] text-[10px] font-bold flex-shrink-0">TIE</span>}
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span className="text-white font-bold text-sm" style={{fontFamily: 'JetBrains Mono'}}>{r.final_time.toFixed(3)}</span>
                            {payout && payout.amount > 0 && (
                              <span className="px-2 py-0.5 bg-[#34C759]/15 text-[#34C759] rounded text-xs font-bold" data-testid={`sidepot-payout-${r.place}`}>
                                ${payout.amount.toFixed(0)}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Payout Summary (overall only) */}
                  {sidepotResults.payouts && sidepotResults.payouts.length > 0 && (
                    <div className="mt-3 p-3 bg-[#0A0A0A] border border-[#262626] rounded-lg">
                      <p className="text-xs font-semibold text-[#9E9E9E] mb-2">Payout Breakdown</p>
                      <div className="space-y-1">
                        {sidepotResults.payouts.map(p => (
                          <div key={p.place} className="flex justify-between text-xs">
                            <span className="text-white">{p.place}{p.place===1?'st':p.place===2?'nd':p.place===3?'rd':'th'} Place {p.tied ? `(T-${p.split_count})` : ''}</span>
                            <span className="text-[#34C759] font-bold">${p.amount.toFixed(0)} <span className="text-[#525252]">({p.percentage}%)</span></span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {sidepotLoading && (
            <div className="text-center py-4"><div className="shimmer h-6 w-6 rounded-full mx-auto"></div></div>
          )}
        </>
      )}

      {(!show.sidepots || show.sidepots.length === 0) && (
        <div className="text-center py-8">
          <Lightning size={40} className="text-[#262626] mx-auto mb-3" />
          <p className="text-[#9E9E9E] text-sm">No sidepots configured for this show</p>
          <p className="text-[#555] text-xs mt-1">Add sidepots when creating the show</p>
        </div>
      )}
    </div>
  );
};
